// How TUNE Analytics words a car: what it is, its trip computer and its next service, in the car's own units and as
// the TUNE MFD words them (TUNE Live's trips.rs), so the web and the MFD agree.
import { symbol, toDisplay, type components, type Units } from '@tune/shared';

type Vehicle = components['schemas']['Vehicle'];
type Fuel = components['schemas']['Fuel'];

/** What the car is, from what's given: "1999 Nissan Skyline GT-R". */
export function describeCar(car: Pick<Vehicle, 'year' | 'make' | 'model'>): string {
  return [car.year, car.make, car.model].filter((part) => part !== null && part !== '').join(' ');
}

/** A number grouped by thousands, to so many places: "48,213.4". */
export function grouped(value: number, places = 0): string {
  return value.toLocaleString('en-AU', { minimumFractionDigits: places, maximumFractionDigits: places });
}

/** A distance in the car's units, to a tenth: "412.6 km", "256.4 mi". */
export function distanceText(units: Units, km: number): string {
  return `${grouped(toDisplay(units, 'distance', km), 1)} ${symbol(units, 'distance')}`;
}

/** A speed in the car's units: "184 km/h". */
export function speedText(units: Units, kmh: number): string {
  return `${grouped(toDisplay(units, 'speed', kmh))} ${symbol(units, 'speed')}`;
}

/** How long, as the MFD says it: "5 h 12 min", "41 min", "30 s". */
export function durationText(seconds: number): string {
  const s = Math.round(Math.max(0, seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor(s / 60) % 60;
  if (h === 0) {
    return m === 0 ? `${s} s` : `${m} min`;
  }
  return `${h} h ${m} min`;
}

/** A trip's figures: how long, its average speed when it took any time, and its top speed. */
export function tripParts(units: Units, trip: { distanceKm: number; timeS: number; maxKmh: number }): string[] {
  const parts = [durationText(trip.timeS)];
  if (trip.timeS > 0) {
    parts.push(`average ${speedText(units, trip.distanceKm / (trip.timeS / 3600))}`);
  }
  parts.push(`top ${speedText(units, trip.maxKmh)}`);
  return parts;
}

/** A trip's figures in a line: "5 h 12 min · average 79 km/h · top 184 km/h". */
export function tripText(units: Units, trip: { distanceKm: number; timeS: number; maxKmh: number }): string {
  return tripParts(units, trip).join(' · ');
}

/**
 * The next service as the MFD says it: "Due in 2,340 km", "Due in 12.5 h", "Overdue by 150 mi". Null when the MFD
 * hasn't said.
 */
export function serviceText(
  due: { km: number | null; hours: number | null } | null,
  units: Units,
): { text: string; overdue: boolean } | null {
  if (due?.km != null) {
    const shown = `${grouped(toDisplay(units, 'distance', Math.abs(due.km)))} ${symbol(units, 'distance')}`;
    return due.km < 0 ? { text: `Overdue by ${shown}`, overdue: true } : { text: `Due in ${shown}`, overdue: false };
  }
  if (due?.hours != null) {
    const shown = `${grouped(Math.abs(due.hours), 1)} h`;
    return due.hours < 0 ? { text: `Overdue by ${shown}`, overdue: true } : { text: `Due in ${shown}`, overdue: false };
  }
  return null;
}

/** What a car can run on, in words, in the order they're offered. */
export const FUELS: ReadonlyArray<readonly [Fuel, string]> = [
  ['petrol', 'Petrol'],
  ['petrolE10', 'Petrol E10'],
  ['diesel', 'Diesel'],
  ['e85', 'E85'],
  ['lpg', 'LPG'],
  ['electric', 'Electric'],
  ['other', 'Something else'],
];

export function fuelText(fuel: Fuel | null): string | null {
  return FUELS.find(([f]) => f === fuel)?.[1] ?? null;
}

const WEEK_MS = 7 * 86_400_000;

/** Whether a device was seen in the last week: its dot is green. */
export function seenThisWeek(lastSeenAt: string | null, now = Date.now()): boolean {
  return lastSeenAt !== null && now - Date.parse(lastSeenAt) < WEEK_MS;
}
